import { Injectable, Provider } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, Data, RouterStateSnapshot, TitleStrategy } from '@angular/router';

import { combineLatest, first } from 'rxjs';

import { TranslocoService } from '@jsverse/transloco';

@Injectable()
export class CustomTitleStrategy extends TitleStrategy {
  constructor(
    private readonly _meta: Meta,
    private readonly _title: Title,
    private readonly _transloco: TranslocoService
  ) {
    super();
  }

  private _findRouteData(activatedRouteSnapshot: ActivatedRouteSnapshot): Data {
    const findData = ({ firstChild, data }: ActivatedRouteSnapshot): ActivatedRouteSnapshot | Data => (firstChild ? findData(firstChild) : data);

    return findData(activatedRouteSnapshot);
  }

  override updateTitle(routerState: RouterStateSnapshot): void {
    if ([
        '/activity/',
        '/profile/'
      ].some(path => routerState.url.includes(path)) && routerState.url.endsWith('/overview')) {
      return;
    }

    const { meta }: Data = this._findRouteData(routerState.root);

    combineLatest([
      /**
       * t(META.TITLES.DEFAULT)
       * t(META.DESCRIPTIONS.DEFAULT)
       */
      this._transloco.selectTranslate(meta?.description || 'seo.default.description'),
      this._transloco.selectTranslate(meta?.keywords || 'seo.default.keywords'),
      this._transloco.selectTranslate(meta?.title || 'seo.default.title')
    ])
      .pipe(first())
      .subscribe(
        ([
          descriptionI18N,
          keywordsI18N,
          titleI18N
        ]: ReadonlyArray<string>) => {
          this._title.setTitle(titleI18N);

          this._meta.updateTag({
            name: 'description',
            content: descriptionI18N
          });

          this._meta.updateTag({
            name: 'keywords',
            content: keywordsI18N
          });

          // NOTE: update also OG meta tags
          this._meta.updateTag({
            name: 'og:title',
            content: titleI18N
          });

          this._meta.updateTag({
            name: 'og:description',
            content: descriptionI18N
          });

          this._meta.updateTag({
            name: 'og:url',
            content: routerState.url
          });

          this._meta.updateTag({
            name: 'og:image',
            content: 'https://files.besmarthead.com/social/sh-cover.png'
          });
        }
      );
  }
}

export const titleStrategyProvider: Provider = {
  deps: [
    Meta,
    Title,
    TranslocoService
  ],
  provide: TitleStrategy,
  useClass: CustomTitleStrategy
};
